import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../components/Navigation';
import { Coins, Sparkles, TrendingUp, Star, Zap, ArrowRight, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useCreditsStore } from '../../store/creditsStore';

interface CreditPack {
  id: string;
  name: string;
  credits_amount: number;
  price: number;
  description: string;
  is_active: boolean;
}

interface CreditUse {
  name: string;
  cost: number;
  icon: typeof Sparkles;
  description: string;
  color: string;
  link?: string;
}

export function CreditsStore() {
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const { balance } = useCreditsStore();

  useEffect(() => {
    loadCreditPacks();
  }, []);

  const loadCreditPacks = async () => {
    try {
      const { data, error } = await supabase
        .from('credit_packs')
        .select('*')
        .eq('is_active', true)
        .order('price', { ascending: true });

      if (error) throw error;
      setCreditPacks(data || []);
    } catch (error) {
      console.error('Error loading credit packs:', error);
    } finally {
      setLoading(false);
    }
  };

  const creditUses: CreditUse[] = [
    {
      name: 'Build New Resume',
      cost: 10,
      icon: Sparkles,
      description: 'AI-generated professional resume from scratch',
      color: 'cyan',
      link: '/jobseeker/resume-builder',
    },
    {
      name: 'Refresh Resume',
      cost: 8,
      icon: TrendingUp,
      description: 'Completely rewrite and optimize your existing resume',
      color: 'blue',
      link: '/jobseeker/resumes',
    },
    {
      name: 'Cover Letter',
      cost: 5,
      icon: Star,
      description: 'AI-generated cover letter tailored to any job',
      color: 'orange',
      link: '/swipe',
    },
    {
      name: 'Add Job to Resume',
      cost: 4,
      icon: Star,
      description: 'Add new work experience with AI-enhanced bullets',
      color: 'amber',
      link: '/jobseeker/resumes',
    },
    {
      name: 'Improve Bullet Points',
      cost: 2,
      icon: Sparkles,
      description: 'Make your achievements more impactful',
      color: 'green',
      link: '/jobseeker/resumes',
    },
    {
      name: 'Entry-Level Job Application',
      cost: 2,
      icon: Zap,
      description: 'AI applies for you with customized resume',
      color: 'cyan',
      link: '/swipe',
    },
    {
      name: 'Mid-Level Job Application',
      cost: 3,
      icon: Zap,
      description: 'AI applies for you with customized resume',
      color: 'blue',
      link: '/swipe',
    },
    {
      name: 'High-Paying Job Application',
      cost: 5,
      icon: Zap,
      description: 'AI applies for senior/high-salary positions',
      color: 'orange',
      link: '/swipe',
    },
  ];

  const handlePurchase = async (packId: string) => {
    console.log('Purchase credit pack:', packId);
  };

  const getBestValueBadge = (index: number, total: number) => {
    if (index === Math.floor(total / 2)) {
      return (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-1 rounded-full text-xs font-bold shadow-lg">
            Best Value
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-3xl p-8 mb-12 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Coins className="w-10 h-10" />
                <h1 className="text-4xl font-bold">Credits Store</h1>
              </div>
              <p className="text-white/90 text-lg">
                Small boosts. Big results.
              </p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-8 py-4 rounded-2xl">
              <div className="text-sm text-white/80 mb-1">Your Balance</div>
              <div className="text-4xl font-bold">{balance}</div>
              <div className="text-sm text-white/80">credits</div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-3 text-center">
            Buy Credit Packs
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Choose the pack that fits your goals
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {creditPacks.map((pack, index) => {
              const creditsPerDollar = pack.credits_amount / pack.price;
              const isBestValue = index === Math.floor(creditPacks.length / 2);

              return (
                <div
                  key={pack.id}
                  className={`relative bg-white rounded-2xl p-6 border-2 transition-all hover:shadow-xl ${
                    isBestValue
                      ? 'border-orange-500 scale-105'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {getBestValueBadge(index, creditPacks.length)}

                  <div className="text-center mb-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl mb-3">
                      <Coins className="w-8 h-8 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {pack.name}
                    </h3>
                    <p className="text-sm text-gray-600">{pack.description}</p>
                  </div>

                  <div className="text-center mb-4">
                    <div className="text-4xl font-bold text-gray-900 mb-1">
                      {pack.credits_amount}
                    </div>
                    <div className="text-sm text-gray-600">credits</div>
                  </div>

                  <div className="text-center mb-6">
                    <div className="text-3xl font-bold text-orange-600 mb-1">
                      ${pack.price.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {creditsPerDollar.toFixed(1)} credits per dollar
                    </div>
                  </div>

                  <button
                    onClick={() => handlePurchase(pack.id)}
                    className={`w-full py-3 px-6 rounded-xl font-bold transition-all ${
                      isBestValue
                        ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 shadow-lg hover:shadow-xl'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    Purchase
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-3 text-center">
            Ways to Use Credits
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Unlock premium features and boost your job search
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {creditUses.map((use) => {
              const Icon = use.icon;
              const canAfford = balance >= use.cost;

              return (
                <div
                  key={use.name}
                  className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-cyan-300 transition-all hover:shadow-lg"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div
                      className={`w-12 h-12 bg-gradient-to-br ${
                        use.color === 'orange'
                          ? 'from-orange-500 to-amber-500'
                          : use.color === 'cyan'
                          ? 'from-cyan-500 to-blue-500'
                          : use.color === 'blue'
                          ? 'from-blue-500 to-cyan-500'
                          : use.color === 'purple'
                          ? 'from-purple-500 to-pink-500'
                          : 'from-amber-500 to-orange-500'
                      } rounded-xl flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {use.name}
                      </h3>
                      <p className="text-sm text-gray-600">{use.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <Coins className="w-5 h-5 text-orange-500" />
                      <span className="font-bold text-gray-900">
                        {use.cost} credits
                      </span>
                    </div>
                    {use.link ? (
                      <Link
                        to={use.link}
                        className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                          canAfford
                            ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed pointer-events-none'
                        }`}
                      >
                        Use Feature
                      </Link>
                    ) : (
                      <button
                        disabled={!canAfford}
                        className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                          canAfford
                            ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Use
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl p-10 text-center text-white">
          <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-90" />
          <h2 className="text-3xl font-bold mb-4">
            Want Unlimited Credits?
          </h2>
          <p className="text-xl mb-6 text-white/90 max-w-2xl mx-auto">
            Upgrade to ResuSwipe+ and get 2x credit rewards on every action, plus exclusive features.
          </p>
          <a
            href="/pricing"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-cyan-600 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
          >
            View Premium Plans
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>

        <div className="mt-12 bg-white rounded-2xl p-8 border-2 border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            How Credits Work
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Check className="w-5 h-5 text-cyan-500" />
                Earn Credits
              </h4>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• 100 credits signup bonus</li>
                <li>• 1 credit per job interest (swipe right)</li>
                <li>• 10 credits for completing your profile</li>
                <li>• 15 credits for uploading your resume</li>
                <li>• 25 credits when a friend signs up with your code</li>
                <li>• 75 more credits when they subscribe to premium</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Check className="w-5 h-5 text-orange-500" />
                Spend Credits
              </h4>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• Boost your profile visibility</li>
                <li>• Unlock premium job listings</li>
                <li>• Get AI-powered career insights</li>
                <li>• Save jobs with reminders</li>
                <li>• Access exclusive features</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
